import { Fragment, useMemo } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { ChevronUpDownIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

/**
 * A search box over a list that also accepts a value which is not in it.
 *
 * The value is a plain **string**, never an option object, and that is the
 * whole design. Two requirements fall out of it for free:
 *
 *   * an operator can register a facility the upstream directory has never
 *     heard of — they type it and it is simply the value;
 *   * editing an existing record shows whatever was stored, even when no
 *     option matches it, with no "value not found" flash and no phantom
 *     clearing.
 *
 * Picking an option from the list is therefore a *side effect* (`onSelect`),
 * not the value itself. That is what lets the caller fill in coordinates and a
 * city when — and only when — a real directory row was chosen.
 */

export interface CreatableComboboxProps<T> {
  value: string;
  /** Called on every keystroke and on selection: free text is committed as typed. */
  onChange: (value: string) => void;
  /** Called ONLY when an option from the list is chosen, never on free text. */
  onSelect?: (option: T) => void;
  options: T[];
  getKey: (option: T) => string | number;
  getLabel: (option: T) => string;
  /** Muted second line inside an option, e.g. the city and facility type. */
  renderMeta?: (option: T) => React.ReactNode;
  query: string;
  onQueryChange: (query: string) => void;
  isLoading?: boolean;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  /** Label for the "use what I typed" entry. */
  createLabel?: (query: string) => string;
  onBlur?: () => void;
  id?: string;
}

const CREATE_SENTINEL = Symbol('create');

export default function CreatableCombobox<T>({
  value,
  onChange,
  onSelect,
  options,
  getKey,
  getLabel,
  renderMeta,
  query,
  onQueryChange,
  isLoading = false,
  error = false,
  disabled = false,
  placeholder,
  emptyMessage = 'لا توجد نتائج',
  createLabel = (q) => `استخدام «${q}» كما هو`,
  onBlur,
  id,
}: CreatableComboboxProps<T>) {
  const trimmedQuery = query.trim();

  // Offer "use what I typed" unless the list already contains exactly it.
  const showCreate = useMemo(() => {
    if (!trimmedQuery) return false;
    return !options.some((o) => getLabel(o).trim() === trimmedQuery);
  }, [trimmedQuery, options, getLabel]);

  return (
    <Combobox
      value={value}
      immediate
      disabled={disabled}
      onChange={(selected: unknown) => {
        if (selected === CREATE_SENTINEL) {
          onChange(trimmedQuery);
          return;
        }
        if (selected == null) return;
        const option = selected as T;
        onChange(getLabel(option));
        onSelect?.(option);
      }}
    >
      <div className="relative">
        <ComboboxInput
          id={id}
          className={clsx('input pl-9', error && 'input-error')}
          placeholder={placeholder}
          autoComplete="off"
          displayValue={() => value}
          onBlur={onBlur}
          onChange={(event) => {
            // Committed as typed, so nothing is lost by walking away from the
            // dropdown without picking anything. This is what makes the
            // "creatable" part real rather than decorative.
            onQueryChange(event.target.value);
            onChange(event.target.value);
          }}
        />
        <ComboboxButton className="absolute inset-y-0 left-0 flex items-center px-2 text-gray-400">
          <ChevronUpDownIcon className="w-5 h-5" aria-hidden="true" />
        </ComboboxButton>

        {/* `anchor` portals the panel out of the DOM flow. Required: this sits
            inside a modal whose body scrolls, and an in-flow absolute panel
            would be clipped by it. `bottom start` is direction-logical, so it
            pins to the right edge under dir="rtl" without a hardcoded side. */}
        <ComboboxOptions
          anchor={{ to: 'bottom start', gap: 4 }}
          className={clsx(
            'z-[100] w-[var(--input-width)] max-h-60 overflow-auto rounded-lg border',
            'border-gray-200 bg-white py-1 shadow-lg text-right text-sm',
            '3xl:text-base 3xl:rounded-xl 4xl:text-lg empty:invisible',
          )}
        >
          {isLoading && (
            <div className="px-3 py-2 text-gray-500">جارٍ التحميل…</div>
          )}

          {!isLoading && options.length === 0 && !showCreate && (
            <div className="px-3 py-2 text-gray-500">{emptyMessage}</div>
          )}

          {options.map((option) => (
            <ComboboxOption key={getKey(option)} value={option} as={Fragment}>
              {({ focus, selected }) => (
                <li
                  className={clsx(
                    'cursor-pointer px-3 py-2 flex items-start gap-2',
                    focus ? 'bg-primary-50' : 'bg-white',
                  )}
                >
                  <CheckIcon
                    className={clsx('w-4 h-4 mt-0.5 shrink-0',
                      selected ? 'text-primary-600' : 'invisible')}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-gray-900">{getLabel(option)}</span>
                    {renderMeta && (
                      <span className="block truncate text-xs text-gray-500">
                        {renderMeta(option)}
                      </span>
                    )}
                  </span>
                </li>
              )}
            </ComboboxOption>
          ))}

          {showCreate && (
            // A real option, so keyboard users reach it with the arrow keys
            // rather than having to know that plain typing already worked.
            <ComboboxOption value={CREATE_SENTINEL} as={Fragment}>
              {({ focus }) => (
                <li
                  className={clsx(
                    'cursor-pointer px-3 py-2 flex items-center gap-2 border-t border-gray-100',
                    focus ? 'bg-primary-50' : 'bg-white',
                  )}
                >
                  <PlusIcon className="w-4 h-4 text-primary-600 shrink-0" />
                  <span className="truncate text-primary-700">{createLabel(trimmedQuery)}</span>
                </li>
              )}
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
